import { useMsal } from '@azure/msal-react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, mergeClasses, Option, Switch, Text } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { Notification, NotificationLocationCode, NotificationRequest } from '../../../models';
import { ValidationMessageList } from '../../shared-components';
import { FormInput, SelectOptions } from '../../shared-components/form';
import { LoadingSpinner } from '../../shared-components/LoadingSpinner';
import './AddOrEditNotificationModal.scss';

interface AddOrEditNotificationModalProps {
  isModalOpen: boolean;
  selectedNotification?: Notification;
  onSuccess: () => void;
  onDismissModal: () => void;
}

type LoadingType = 'load' | 'add' | 'update';

const AddOrEditNotificationModalImpl: React.FC<AddOrEditNotificationModalProps> = (props) => {
  const {
    isModalOpen,
    selectedNotification,
    onSuccess,
    onDismissModal
  } = props;

  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    notificationStore,
    notificationPageUiStore,
  } = useStores();

  const commonStyle = useCommonStyles();

  const [loadingType, setLoadingType] = useState<LoadingType>();
  const [isBeingValidated, setIsBeingValidated] = useState<boolean>(false);

  const handleDismissModal = () => {
    onDismissModal();
    setIsBeingValidated(false);
  };

  const loadingText = (): string => {
    if (loadingType === 'load') return 'Loading Notification';
    if (loadingType === 'add') return 'Adding Notification';
    if (loadingType === 'update') return 'Updating Notification';
    return '';
  };

  const onAddNotificationButtonClicked = async (): Promise<void> => {
    setIsBeingValidated(true);
    if (!notificationPageUiStore.notificationRequestBody) return;
    if (notificationPageUiStore.notificationRequestValidation.isValid) {
      setLoadingType('add');
      try {
        await apiStore.createNotification(instance, notificationPageUiStore.notificationRequestBody);

        onSuccess();
        onDismissModal();
      } catch (error) {
        // TODO - Handle error
      } finally {
        setLoadingType(undefined);
      }
    }
  };

  const onUpdateNotificationButtonClicked = async (): Promise<void> => {
    setIsBeingValidated(true);
    if (!notificationPageUiStore.notificationRequestBody || !selectedNotification) return;
    if (notificationPageUiStore.notificationRequestValidation.isValid) {
      setLoadingType('update');
      try {
        await apiStore.updateNotification(
          instance,
          selectedNotification.notificationId,
          notificationPageUiStore.notificationRequestBody,
        );

        onSuccess();
        onDismissModal();
      } catch (error) {
        // TODO - Handle error
      } finally {
        setLoadingType(undefined);
      }
    }
  };

  const preloadData = (): void => {
    setLoadingType('load');

    if (selectedNotification) {
      notificationPageUiStore.createNotificationRequestBodyForUpdate(selectedNotification);
    } else {
      notificationPageUiStore.createNotificationRequestBody();
    }

    setLoadingType(undefined);
  };

  const generateNotificationLocationDropdownValue = useMemo(() => (
    notificationStore.notificationLocations.find((notificationLocation) => notificationLocation.locationCode === notificationPageUiStore.notificationRequestBody?.locationCode)?.displayName ?? ''
  ), [notificationStore.notificationLocations.join(''), notificationPageUiStore.notificationRequestBody?.locationCode]);

  const generateNotificationTypeDropdownValue = useMemo(() => (
    notificationStore.nonSuccessNotificationTypes.find((notificationLocation) => notificationLocation.typeCode === notificationPageUiStore.notificationRequestBody?.typeCode)?.displayName ?? ''
  ), [notificationStore.nonSuccessNotificationTypes.join(''), notificationPageUiStore.notificationRequestBody?.typeCode]);

  useEffect(() => {
    preloadData();
    return () => {
      setIsBeingValidated(false);
      notificationPageUiStore.resetModalStates();
    };
  }, []);

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={(_, data) => {
        if (!data.open) handleDismissModal();
      }}
    >
      <DialogSurface
        className={mergeClasses(commonStyle.modalSurface, 'add-or-edit-notification-modal')}
      >
        <DialogBody className={commonStyle.modalBody}>
          {
            !!loadingType && (
              <LoadingSpinner
                label={loadingText()}
              />
            )
          }
          <DialogTitle
            action={(
              <Button
                appearance='transparent'
                title='Dismiss'
                icon={<DismissRegular />}
                onClick={handleDismissModal}
              />
            )}
          >
            {selectedNotification ? 'Update notification' : 'Add notification'}
          </DialogTitle>
          <DialogContent>
            <SelectOptions
              required
              label='Notification location'
              validationMessage={
                isBeingValidated
                && notificationPageUiStore.notificationRequestValidation.fieldErrors.locationCode?.length
                  ? (
                    <ValidationMessageList
                      messages={notificationPageUiStore.notificationRequestValidation.fieldErrors.locationCode}
                    />
                  )
                  : undefined
              }
              onOptionSelect={(data) => notificationPageUiStore.setNotificationRequestBody({
                key: 'locationCode',
                value: data.optionValue as NotificationLocationCode,
              })}
              dropdownProps={{
                value: generateNotificationLocationDropdownValue,
                selectedOptions: [notificationPageUiStore.notificationRequestBody?.locationCode ?? 'partner-homepage'],
              }}
            >
              {notificationStore.notificationLocations.map((option) => (
                <Option key={option.displayName} value={option.locationCode}>
                  {option.displayName}
                </Option>
              ))}
            </SelectOptions>
            <SelectOptions
              required
              label='Type of notification'
              dropdownProps={{
                value: generateNotificationTypeDropdownValue,
                selectedOptions: [notificationPageUiStore.notificationRequestBody?.typeCode ?? ''],
              }}
              onOptionSelect={(data) => notificationPageUiStore.setNotificationRequestBody({
                key: 'typeCode',
                value: data.optionValue as NotificationRequest['typeCode'],
              })}
              validationMessage={
                isBeingValidated
                && notificationPageUiStore.notificationRequestValidation.fieldErrors.typeCode?.length
                  ? (
                    <ValidationMessageList
                      messages={notificationPageUiStore.notificationRequestValidation.fieldErrors.typeCode}
                    />
                  )
                  : undefined
              }
            >
              {notificationStore.nonSuccessNotificationTypes.map((option) => (
                <Option key={option.notificationTypeId} value={option.typeCode}>
                  {option.displayName}
                </Option>
              ))}
            </SelectOptions>
            <FormInput
              required
              label='Message'
              onChange={(value) => notificationPageUiStore.setNotificationRequestBody({
                key: 'message',
                value: value,
              })}
              validationMessage={
                isBeingValidated
                && notificationPageUiStore.notificationRequestValidation.fieldErrors.message?.length
                  ? (
                    <ValidationMessageList
                      messages={notificationPageUiStore.notificationRequestValidation.fieldErrors.message}
                    />
                  )
                  : undefined
              }
              textAreaProps={{
                value: notificationPageUiStore.notificationRequestBody?.message ?? '',
              }}
            />
            <div>
              <Text block weight='semibold'>Is Active</Text>
              <Switch
                label={notificationPageUiStore.selectedNotification?.isActive ? 'Yes' : 'No'}
                checked={notificationPageUiStore.selectedNotification?.isActive ?? false}
                disabled
              />
            </div>
          </DialogContent>
          <DialogActions>
            {
              !selectedNotification && (
                <Button
                  appearance='primary'
                  disabled={isBeingValidated ? !notificationPageUiStore.notificationRequestValidation.isValid : undefined}
                  onClick={() => onAddNotificationButtonClicked()}
                >
                  Add notification
                </Button>
              )
            }
            {
              selectedNotification && (
                <Button
                  appearance='primary'
                  disabled={isBeingValidated ? !notificationPageUiStore.notificationRequestValidation.isValid : undefined}
                  onClick={() => onUpdateNotificationButtonClicked()}
                >
                  Update notification
                </Button>
              )
            }
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export const AddOrEditNotificationModal = observer(AddOrEditNotificationModalImpl);
